import { CareerId } from '@common/const/catalog/career';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { ClientType } from '@common/model/client/clientType';
import { useIsLoggedIn } from '@cp/entities/currentUser';
import { useCallback } from 'react';
import { AsyncResult, Result } from '@youhelp-ru/core';
import { ClientProfileViewModel } from '@common/model/client';
import { useHasActiveSubscription } from './useHasActiveSubscription';

interface SPClientSelection {
  careerId: CareerId;
  catalogType: CatalogItemType;
  clientType: ClientType;
  cityId?: number;
}

interface SPParams {
  events?: SPEvents;
  provide?: SPProvide;
  disabledProtection?: boolean;
}

export interface SPProvide {
  subCase?: () => void;
  authCase?: (clientSelction?: SPClientSelection) => AsyncResult<ClientProfileViewModel, Error>;
}

export interface SPEvents {
  onAuthSuccess?: (clientProfile: ClientProfileViewModel, clientSelection?: SPClientSelection) => void;
  onAuthError?: (clientSelection?: SPClientSelection) => void;
  onSPSuccess?: () => void;
}

export const useSubscriptionProtection = (params?: SPParams) => {
  const isLoggedIn = useIsLoggedIn();
  const subscribed = useHasActiveSubscription();

  return useCallback(
    (clientSelection?: SPClientSelection) => {
      const { onAuthSuccess, onAuthError } = params?.events || {};

      if (!params?.disabledProtection && !isLoggedIn && params?.provide?.authCase) {
        params.provide
          .authCase(clientSelection)
          .then((result) => {
            if (result.isErr) {
              onAuthError?.(clientSelection);
              return;
            }

            if (!isLoggedIn) {
              onAuthSuccess?.(result.unwrap(), clientSelection);
            }
          })
          .catch(() => onAuthError?.(clientSelection));
        return Result.Err(new Error('Unauthorized'));
      }

      if (!params?.disabledProtection && !subscribed && params?.provide?.subCase) {
        params.provide.subCase();
        return Result.Err(new Error('Unsubscribe'));
      }

      params?.events?.onSPSuccess?.();
      return Result.Ok();
    },
    [isLoggedIn, params?.disabledProtection, params?.events, params?.provide, subscribed],
  );
};
